import {BlockTypeSelect, headingsPlugin, ListsToggle} from '@mdxeditor/editor'
import { MDXEditor, UndoRedo, BoldItalicUnderlineToggles, toolbarPlugin, listsPlugin, linkPlugin, DiffSourceToggleWrapper,diffSourcePlugin } from '@mdxeditor/editor'
import '@mdxeditor/editor/style.css'
import React, {useEffect, useState, useMemo, DragEvent} from "react";
import {Box, TextField, MenuItem, Select, InputLabel, FormControl, Checkbox, ListItemText, SelectChangeEvent, Button, Typography, Grid, IconButton, List, ListItem, Switch, FormControlLabel, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Card, CardContent, CardActions, Chip, Divider } from "@mui/material";
import {useApplicationContext}from "../../ApplicationContext";
import {GetFileTypeData, GetFilterTagData, GetHostSystemData, ImageDto, AddFileTypeData, FileTypeVariantsDto, FileTypeClass, FileTypePlatformConfigurationDto, FileTypeLicenseOptionResponse, LicenseOptionDto, LicenseOptionAggregationMode, LicenseOptionType, AddLicenseOptionToFileTypeRequest, CreateLicenseOptionRequest, UpdateFileTypeLicenseOptionRequest}from "../../apiClient/Api";
import { useParams } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import iconPlaceholder from './icon_placeholder.svg';
import IconPlaceholder from './IconPlaceholder';
import ScreenshotPlaceholder from './ScreenshotPlaceholder';
import PhonePlaceholder from './PhonePlaceholder';
import { useFileTypeClassTranslations } from 'Hooks/FileTypeClass';
import {useTranslation} from "react-i18next";
import {useSnackbar} from "notistack";
import { LicenseOptionsSection } from './LicenseOptionsSection';
import { LicenseOptionsOverviewDialog } from '../../Components/Admin/LicenseOptionsOverviewDialog';

function FileTypeEdit() {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const context = useApplicationContext();
    const controller = context.api.fileType;
    const { id } = useParams<{ id: string }>();
    const [isFetching, setIsFetching] = useState<boolean>(false);
    const [licenseOptionsOverviewOpen, setLicenseOptionsOverviewOpen] = useState<boolean>(false);

    const [hostSystems, setHostSystems] = useState<GetHostSystemData[]>([]);
    const [filterTags, setFilterTags] = useState<GetFilterTagData[]>([]);
    const [fileTypes, setFileType] = useState<GetFileTypeData|null>(null);
    const [value, setValue] = useState<string>('');
    const [icon, setIcon] = useState<File | null>(null);
    const [images, setImages] = useState<{ file: File, description: string }[]>([]);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [logo, setLogo] = useState<ImageDto | null>(null);

    const fileTypeTranslations = useFileTypeClassTranslations();

    const [newVariant, setNewVariant] = useState<FileTypeVariantsDto>({ name: '', tag: '' });
    const [newPlatformHostId, setNewPlatformHostId] = useState<string>("");

    const [screenshots, setScreenshots] = useState<ImageDto[]>();

    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [isAppStore, setIsAppStore] = useState<boolean>(false);
    const [appStoreFileTypeId, setAppStoreFileTypeId] = useState<string | null>(null);

    // License Options States
    const [licenseOptions, setLicenseOptions] = useState<FileTypeLicenseOptionResponse[]>([]);
    const [availableLicenseOptions, setAvailableLicenseOptions] = useState<LicenseOptionDto[]>([]);
    const [selectedLicenseOptionId, setSelectedLicenseOptionId] = useState<string>('');
    const [isRequired, setIsRequired] = useState<boolean>(false);
    const [fileTypeSpecificDefault, setFileTypeSpecificDefault] = useState<string>('');
    const [serviceEinheiten, setServiceEinheiten] = useState<string>('');

    const logoUrl = useMemo(() => {
            // @ts-ignore
        var serverUrl = ServerConfig.SERVER_URL;
        if (logo) {
            return `${serverUrl}/FileType/Image/${logo.id}`;
        }
        

        return null;
    }, [logo]);

    const initialMarkdown = useMemo(() => {
        return fileTypes?.description || '';
    }, [fileTypes]);

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        setValue(initialMarkdown);
    }, [initialMarkdown]);

    async function updateLogoData() {
        var logoData = await controller.fileTypeGetLogo(id!);

        setLogo(logoData.data);
    }

    async function updateScreenshotData() {
        var logoData = await controller.fileTypeGetScreenshots(id!);
        setScreenshots(logoData.data);
    }

    /**
     * Loads all license options assigned to this FileType
     */
    async function loadLicenseOptions() {
        if (!id) return;
        try {
            const response = await context.api.api.licenseGetFileTypeLicenseOptions(id);
            setLicenseOptions(response.data);
        } catch (e) {
            context.apiError(e);
        }
    }

    /**
     * Loads all available license options from the system
     */
    async function loadAvailableLicenseOptions() {
        try {
            const response = await context.api.api.licenseGetLicenseOptions();
            setAvailableLicenseOptions(response.data);
        } catch (e) {
            context.apiError(e);
        }
    }

    async function fetchData() {
        setIsFetching(true)
        try {
            var hostSystems = await controller.fileTypeGetHostSystems();
            setHostSystems(hostSystems.data);
            var filterTags = await context.api.filterTags.filterTagsListFilterTags();
            setFilterTags(filterTags.data);

            if(id == undefined){
                setFileType(null)
            }
            else{
                var value = await controller.fileTypeGetFileType(id);
                setFileType(value.data)    
            }

            await updateLogoData();

            await updateScreenshotData();
            
            // Load license options
            if (id) {
                await loadLicenseOptions();
                await loadAvailableLicenseOptions();
            }
            
            // Load license options
            await loadLicenseOptions();
            await loadAvailableLicenseOptions();
            
            // Check if this FileType is set as AppStore
            try {
                const appStoreConfig = await context.api.appStore.appStoreGetAppStoreFileTypeId();
                setAppStoreFileTypeId(appStoreConfig.data.fileTypeId || null);
                setIsAppStore(appStoreConfig.data.fileTypeId === id);
            } catch (e) {
                console.error('Failed to fetch AppStore FileType ID:', e);
                setIsAppStore(false);
            }
                
        } catch (e) {
            context.apiError(e)
        }
        setIsFetching(false)
    }

    const handleDescriptionChange = (newValue: string) => {
        setValue(newValue);
        if (fileTypes) {
            setFileType({ ...fileTypes, description: newValue });
        }
    };

    const updatePlatformConfigurationById = (hostSystemId: string, updater: (config: FileTypePlatformConfigurationDto) => FileTypePlatformConfigurationDto) => {
        setFileType((current) => {
            if (!current) {
                return null;
            }
            const list = current.platformConfigurations ?? [];
            const index = list.findIndex((p) => p.hostSystemId === hostSystemId);
            if (index === -1) {
                return current;
            }
            const updatedEntry = updater(list[index]);
            const next = [...list];
            next[index] = updatedEntry;
            return {
                ...current,
                platformConfigurations: next,
            };
        });
    };

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (fileTypes) {

            setFileType({ ...fileTypes, name: event.target.value });
        }
    };

    const handleFilterTagsChange = (event: SelectChangeEvent<(string | undefined)[]>) => {
        const value = event.target.value as string[];
        const selectedTags = filterTags.filter(tag => value.includes(tag.identifier || ''));
        if (fileTypes) {
            setFileType({ ...fileTypes, filterTags: selectedTags });
        }
    };

    const handleMatchingFilesChange = (event: React.ChangeEvent<HTMLInputElement>, hostSystemId: string) => {
        updatePlatformConfigurationById(hostSystemId, (config) => ({
            ...config,
            matchingFiles: event.target.value.split('\n').map(file => file.trim()),
        }));
    };

    /**
     * Removes a platform configuration entry by its host system identifier.
     * @param hostSystemId identifier of the host system to remove
     */
    const handleRemovePlatform = (hostSystemId: string | undefined) => {
        if (!hostSystemId || !fileTypes) {
            return;
        }
        setFileType({
            ...fileTypes,
            platformConfigurations: (fileTypes.platformConfigurations ?? []).filter((p) => p.hostSystemId !== hostSystemId),
        });
    };

    /**
     * Adds a platform configuration entry for the selected host system.
     */
    const handleAddPlatform = () => {
        if (!newPlatformHostId || !fileTypes) {
            return;
        }
        const exists = fileTypes.platformConfigurations?.some((p) => p.hostSystemId === newPlatformHostId);
        if (exists) {
            setNewPlatformHostId("");
            return;
        }
        const newConfig: FileTypePlatformConfigurationDto = {
            hostSystemId: newPlatformHostId,
            matchingFiles: [],
        };
        setFileType({
            ...fileTypes,
            platformConfigurations: [...(fileTypes.platformConfigurations ?? []), newConfig],
        });
        setNewPlatformHostId("");
    };

    const handlePackageIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (fileTypes) {
            setFileType({ ...fileTypes, packageId: event.target.value });
        }
    };

    const handleVersionChange = (event: React.ChangeEvent<HTMLInputElement>) => {

        var intNumber = parseInt(event.target.value);
        if (fileTypes) {
            setFileType({ ...fileTypes, version: intNumber });
        }
    };


    const handleNewVariantChange = (field: 'name' | 'tag', value: string) => {
        setNewVariant({ ...newVariant, [field]: value });
    };

    const addVariant = () => {
        setFileType(ft => ft != null ? ({ ...ft, variants: [...fileTypes?.variants ?? [], newVariant ] }) : null);
        setNewVariant({ name: '', tag: '' });
    };

    const removeVariant = (tag: String | undefined) => {
        if(tag){
            setFileType(ft => ft != null ? ({ ...ft, variants: [...(fileTypes?.variants ?? []).filter(f => f.tag !== tag)] }) : null);
        }
    };

    const handleImageDelete = async (index: number) => {
        const imageToDelete = screenshots![index];
        await controller.fileTypeDeleteImage(imageToDelete.id!);
        setScreenshots(screenshots!.filter((_, i) => i !== index));
    };

    const handleImageDescriptionChange = async (index: number, description: string) => {
        const updatedScreenshots = [...screenshots!];
        updatedScreenshots[index].description = description;
        setScreenshots(updatedScreenshots);
        await controller.fileTypeEditImageDescription(updatedScreenshots[index].id!, description);
    };

    const uploadIcon = async (file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        await controller.fileTypeAddLogo(id!, {
            File: file,
            Description: "Icon"
        });

        await updateLogoData();
    };

    const uploadImages = async (files: { file: File, description: string }[]) => {
        for (const { file } of files) {
            const response = await controller.fileTypeAddScreenshot(id!, {
                File: file,
                Description: "Screenshot"
            });
            setScreenshots(prevScreenshots => [...prevScreenshots!, response.data]);
        }
    };

    const deleteImage = async (file: File) => {
        await controller.fileTypeDeleteImage(id!);
    };

    const handleIconChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            const img = new Image();
            img.src = URL.createObjectURL(file);
            img.onload = async () => {
                if (img.width === img.height && img.width <= 1024 && img.height <= 1024) {
                    setIcon(file);
                    await uploadIcon(file);
                } else {
                    alert("Icon must be a square and not exceed 1024x1024 pixels.");
                }
            };
        }
    };

    const handleImagesChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const files = Array.from(event.target.files).map(file => ({ file, description: '' }));
            setImages(prevImages => [...prevImages, ...files]);
            await uploadImages(files);
        }
    };

    const handleIconDrop = async (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
        if (event.dataTransfer.files && event.dataTransfer.files[0]) {
            const file = event.dataTransfer.files[0];
            const img = new Image();
            img.src = URL.createObjectURL(file);
            img.onload = async () => {
                if (img.width === img.height && img.width <= 1024 && img.height <= 1024) {
                    setIcon(file);
                    await uploadIcon(file);
                } else {
                    alert("Icon must be a square and not exceed 1024x1024 pixels.");
                }
            };
        }
    };

    const handleImagesDrop = async (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
        if (event.dataTransfer.files) {
            const files = Array.from(event.dataTransfer.files).map(file => ({ file, description: '' }));
            setImages(prevImages => [...prevImages, ...files]);
            await uploadImages(files);
        }
    };

    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
    };

    const convertImageDtoToUrl = (imageDto: ImageDto): string => {
        // @ts-ignore
        var serverUrl = ServerConfig.SERVER_URL;
        return `${serverUrl}/FileType/Image/${imageDto.id}`;
    };

    const renderSelectedTags = (selected: (string | undefined)[]) => {
        return selected
            .map(identifier => filterTags.find(tag => tag.identifier === identifier)?.name)
            .filter(name => name !== undefined)
            .join(', ');
    };

    const renderImagePreviews = () => {
        return screenshots!.map((image, index) => (
            <Box key={index} sx={{ margin: 1, position: 'relative', border: '1px solid grey', borderRadius: '4px' }}>
                <img src={convertImageDtoToUrl(image)} alt={`preview-${index}`} width="100" />
                <IconButton
                    onClick={() => handleImageDelete(index)}
                    sx={{ position: 'absolute', top: 0, right: 0, minWidth: '24px', minHeight: '24px', padding: 0 }}
                >
                    <FontAwesomeIcon icon={faTrash} />
                </IconButton>
                <TextField
                    value={image.description || ''}
                    onChange={(e) => handleImageDescriptionChange(index, e.target.value)}
                    placeholder="Description"
                    variant="outlined"
                    size="small"
                    sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, backgroundColor: 'rgba(255, 255, 255, 0.8)' }}
                />
            </Box>
        ));
    };

    /**
     * Handles the save operation for the file type.
     * @async
     */
    const handleSave = async () => {
        if (fileTypes) {
            const data: AddFileTypeData = {
                name: fileTypes.name,
                description: fileTypes.description,
                filterTags: fileTypes.filterTags?.map(tag => tag.identifier || '') ?? [],
                packageId: fileTypes.packageId,
                variants: fileTypes.variants,
                version: fileTypes.version,
                type: fileTypes.type,
                public: fileTypes.public,
                platformConfigurations: fileTypes.platformConfigurations ?? [],
            };
            await controller.fileTypeEditFileType(id!, data);
            const platformUpdates = (fileTypes.platformConfigurations ?? [])
                .filter((platform) => platform.hostSystemId)
                .map((platform) => controller.fileTypeUpdateFileTypes(
                    id!,
                    platform.hostSystemId!,
                    platform.matchingFiles ?? [],
                ));
            await Promise.all(platformUpdates);
            setIsDialogOpen(true);
        }
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
    };

    /**
     * Handles toggling the AppStore setting for this FileType
     */
    const handleAppStoreToggle = async (checked: boolean) => {
        try {
            if (checked) {
                // Set this FileType as AppStore
                await context.api.appStore.appStoreSetAppStoreFileTypeId(id!);
                setIsAppStore(true);
                setAppStoreFileTypeId(id!);
                enqueueSnackbar(t('fileTypeEdit.appStoreSetSuccess'), { variant: 'success' });
            } else {
                // Cannot unset - would need to set another FileType as AppStore
                enqueueSnackbar(t('fileTypeEdit.appStoreCannotUnset'), { variant: 'warning' });
            }
        } catch (e) {
            context.apiError(e);
            setIsAppStore(!checked); // Revert on error
        }
    };

    /**
     * Adds a license option to this FileType
     */
    const handleAddLicenseOption = async () => {
        if (!id || !selectedLicenseOptionId) {
            enqueueSnackbar(t('fileTypeEdit.selectLicenseOption'), { variant: 'warning' });
            return;
        }

        try {
            const request: AddLicenseOptionToFileTypeRequest = {
                licenseOptionId: selectedLicenseOptionId,
                isRequired: isRequired,
                fileTypeSpecificDefault: fileTypeSpecificDefault || null,
                serviceEinheiten: serviceEinheiten.trim() ? Number(serviceEinheiten) : null
            };

            await context.api.api.licenseAddLicenseOptionToFileType(id, request);
            enqueueSnackbar(t('fileTypeEdit.licenseOptionAdded'), { variant: 'success' });
            
            // Reset form
            setSelectedLicenseOptionId('');
            setIsRequired(false);
            setFileTypeSpecificDefault('');
            setServiceEinheiten('');
            
            // Reload license options
            await loadLicenseOptions();
        } catch (e) {
            context.apiError(e);
        }
    };

    /**
     * Creates a new license option and immediately adds it to this FileType
     */
    const handleCreateNewOption = async (option: CreateLicenseOptionRequest) => {
        if (!id) {
            enqueueSnackbar(t('fileTypeEdit.fileTypeNotLoaded'), { variant: 'error' });
            return;
        }

        try {
            // Create the new license option
            const response = await context.api.api.licenseCreateLicenseOption(option);
            const createdOption = response.data;
            enqueueSnackbar(t('fileTypeEdit.licenseOptions.optionCreated'), { variant: 'success' });

            // Add it to the FileType
            const request: AddLicenseOptionToFileTypeRequest = {
                licenseOptionId: createdOption.id!,
                isRequired: isRequired,
                fileTypeSpecificDefault: option.defaultValue || null,
                serviceEinheiten: serviceEinheiten.trim() ? Number(serviceEinheiten) : null
            };

            await context.api.api.licenseAddLicenseOptionToFileType(id, request);
            enqueueSnackbar(t('fileTypeEdit.licenseOptionAdded'), { variant: 'success' });

            // Reset form
            setIsRequired(false);
            setFileTypeSpecificDefault('');
            setServiceEinheiten('');

            // Reload both license options and available options
            await loadLicenseOptions();
            await loadAvailableLicenseOptions();
        } catch (e) {
            context.apiError(e);
        }
    };

    /**
     * Removes a license option from this FileType
     */
    const handleRemoveLicenseOption = async (optionId: string) => {
        if (!id) return;

        try {
            await context.api.api.licenseRemoveLicenseOptionFromFileType(optionId);
            enqueueSnackbar(t('fileTypeEdit.licenseOptionRemoved'), { variant: 'success' });
            
            // Reload license options
            await loadLicenseOptions();
        } catch (e) {
            context.apiError(e);
        }
    };

    /**
     * Updates a license option on this FileType
     */
    /**
     * Updates a license option on this FileType.
     */
    const handleUpdateLicenseOption = async (
        optionId: string,
        required: boolean,
        specificDefault: string,
        isVisibleToCustomer: boolean,
        aggregationMode: LicenseOptionAggregationMode,
        serviceEinheiten: string,
    ) => {
        if (!id) return;

        try {
            // Update FileTypeLicenseOption (isRequired, fileTypeSpecificDefault)
            const fileTypeRequest: UpdateFileTypeLicenseOptionRequest = {
                isRequired: required,
                fileTypeSpecificDefault: specificDefault.trim() ? specificDefault.trim() : null,
                serviceEinheiten: serviceEinheiten.trim() ? Number(serviceEinheiten) : null
            };

            await context.api.api.licenseUpdateFileTypeLicenseOption(optionId, fileTypeRequest);
            
            // Find the license option to get its ID and current values
            const fileTypeLicenseOption = licenseOptions.find(opt => opt.id === optionId);
            if (fileTypeLicenseOption?.licenseOption) {
                // Update the global LicenseOption (isVisibleToCustomer and other fields)
                const licenseOptionRequest: CreateLicenseOptionRequest = {
                    identifier: fileTypeLicenseOption.licenseOption.identifier,
                    commonName: fileTypeLicenseOption.licenseOption.commonName,
                    description: fileTypeLicenseOption.licenseOption.description,
                    type: fileTypeLicenseOption.licenseOption.type,
                    aggregationMode: aggregationMode,
                    defaultValue: fileTypeLicenseOption.licenseOption.defaultValue,
                    displayOrder: fileTypeLicenseOption.licenseOption.displayOrder,
                    isActive: fileTypeLicenseOption.licenseOption.isActive,
                    isVisibleToCustomer: isVisibleToCustomer
                };

                await context.api.api.licenseUpdateLicenseOption(
                    fileTypeLicenseOption.licenseOptionId!,
                    licenseOptionRequest
                );
            }

            enqueueSnackbar(t('fileTypeEdit.licenseOptionUpdated'), { variant: 'success' });
            await loadLicenseOptions();
        } catch (e) {
            context.apiError(e);
        }
    };

    if(isFetching)
        return <Box>Loading...</Box>

    if(fileTypes == null)
        return <Box>File type not found</Box>

    return <Box sx={{width: "100%"}}>
        <TextField
            label="Name"
            value={fileTypes.name || ''}
            onChange={handleNameChange}
            fullWidth
            margin="normal"
        />

        <Select  label="Art" fullWidth value={fileTypes.type ?? FileTypeClass.UNDEFINED} onChange={(e) => setFileType({ ...fileTypes, type: e.target.value as string as FileTypeClass })}>
            {fileTypeTranslations.map((fileType) => (<MenuItem value={fileType.id}>{fileType.name}</MenuItem>))}
        </Select>

         <FormControlLabel control={<Switch checked={fileTypes.public} onChange={(checked) => {
            setFileType((ft) => {
                if(ft != null){
                    return {...ft, public: checked.target.checked}
                    
                }
                return null;
            });
         }} />} label="Öffentlich anzeigen" />

         <FormControlLabel 
            control={
                <Switch 
                    checked={isAppStore} 
                    onChange={(event) => handleAppStoreToggle(event.target.checked)}
                    disabled={isAppStore && appStoreFileTypeId !== id}
                />
            } 
            label={
                <Box>
                    <Typography>{t('fileTypeEdit.setAsAppStore')}</Typography>
                    {appStoreFileTypeId && appStoreFileTypeId !== id && (
                        <Typography variant="caption" color="text.secondary">
                            {t('fileTypeEdit.appStoreAlreadySet')}
                        </Typography>
                    )}
                </Box>
            }
         />
       
        <FormControl fullWidth margin="normal">
            <InputLabel>Filter Tags</InputLabel>
            <Select
                multiple
                value={fileTypes.filterTags?.map(tag => tag.identifier) || []}
                onChange={handleFilterTagsChange}
                renderValue={renderSelectedTags}
            >
                {filterTags.map((filterTag) => (
                    <MenuItem key={filterTag.identifier} value={filterTag.identifier}>
                        <Checkbox checked={fileTypes.filterTags?.some(tag => tag.identifier === filterTag.identifier) || false} />
                        <ListItemText primary={filterTag.name} />
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
        <Box sx={{ marginY: 2 }}>
            <Typography variant="h6">{t('fileTypeEdit.platformsMatchingFiles')}</Typography>
            {(fileTypes.platformConfigurations ?? []).map((platform) => (
                <Box key={platform.hostSystemId} sx={{ border: '1px solid #ccc', borderRadius: 1, p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        {hostSystems.find((hs) => hs.id === platform.hostSystemId)?.name || platform.hostSystemId}
                    </Typography>
                    <TextField
                        label={t('fileTypeEdit.matchingFiles')}
                        value={platform.matchingFiles?.join('\n') || ''}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleMatchingFilesChange(event, platform.hostSystemId ?? "")}
                        fullWidth
                        margin="normal"
                        multiline
                        rows={4}
                    />
                    <Button color="error" variant="outlined" onClick={() => handleRemovePlatform(platform.hostSystemId)} sx={{ mt: 1 }}>
                        {t('fileTypeEdit.removePlatform')}
                    </Button>
                </Box>
            ))}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <FormControl fullWidth>
                    <InputLabel>{t('fileTypeEdit.addHostSystem')}</InputLabel>
                    <Select
                        value={newPlatformHostId}
                        label={t('fileTypeEdit.addHostSystem')}
                        onChange={(e) => setNewPlatformHostId(e.target.value as string)}
                    >
                        {hostSystems
                            .filter((hs) => !(fileTypes.platformConfigurations ?? []).some((p) => p.hostSystemId === hs.id))
                            .map((hostSystem) => (
                                <MenuItem key={hostSystem.id} value={hostSystem.id}>
                                    {hostSystem.name}
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl>
                <Button variant="outlined" onClick={handleAddPlatform}>{t('fileTypeEdit.addButton')}</Button>
            </Box>
        </Box>
        <TextField
            label="Package ID"
            value={fileTypes.packageId || ''}
            onChange={handlePackageIdChange}
            fullWidth
            margin="normal"
        />
        {/* Lizenz Modus zur Freischaltung der Software*/}
        <TextField
            label="Version"
            value={fileTypes.version || 1}
            onChange={handleVersionChange}
            fullWidth
            type="number"
            inputMode='decimal'
            margin="normal"
        />        
        
        <Box sx={{ marginY: 2 }}>
            <Typography variant="h6">Varianten</Typography>
            <List>
                {fileTypes.variants?.map((variant, index) => (
                    <ListItem key={index}>
                        <ListItemText primary={variant.name} secondary={variant.tag} />
                        <IconButton onClick={() => removeVariant(variant.tag)}>
                            <FontAwesomeIcon icon={faTrash} />
                        </IconButton>
                    </ListItem>
                ))}
            </List>
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                <TextField
                    label="Tag"
                    value={newVariant.tag}
                    onChange={(e) => handleNewVariantChange('tag', e.target.value)}
                    sx={{ marginRight: 2 }}
                />
                <TextField
                    label="Name"
                    value={newVariant.name}
                    onChange={(e) => handleNewVariantChange('name', e.target.value)}
                    sx={{ marginRight: 2 }}
                />
                <Button variant="outlined" onClick={addVariant}>Add Variant</Button>
            </Box>
        </Box>

        {/* License Options Section */}
        {id && (
            <LicenseOptionsSection
                licenseOptions={licenseOptions}
                availableLicenseOptions={availableLicenseOptions}
                selectedLicenseOptionId={selectedLicenseOptionId}
                setSelectedLicenseOptionId={setSelectedLicenseOptionId}
                isRequired={isRequired}
                setIsRequired={setIsRequired}
                fileTypeSpecificDefault={fileTypeSpecificDefault}
                setFileTypeSpecificDefault={setFileTypeSpecificDefault}
                serviceEinheiten={serviceEinheiten}
                setServiceEinheiten={setServiceEinheiten}
                onAddLicenseOption={handleAddLicenseOption}
                onRemoveLicenseOption={handleRemoveLicenseOption}
                onUpdateLicenseOption={handleUpdateLicenseOption}
                onCreateNewOption={handleCreateNewOption}
                onRefreshAvailableOptions={loadAvailableLicenseOptions}
            />
        )}

        {/* Button für Lizenzoptionen-Übersicht */}
        <Box sx={{ marginY: 2, display: 'flex', justifyContent: 'center' }}>
            <Button 
                variant="outlined" 
                onClick={() => setLicenseOptionsOverviewOpen(true)}
                size="large"
            >
                {t('fileTypeEdit.licenseOptions.overview.showAll')}
            </Button>
        </Box>

        <Box sx={{ marginY: 2 }}>
            <Typography variant="h6">Icon</Typography>
            <Box
                sx={{
                    border: '2px dashed grey',
                    padding: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    minHeight: '150px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: isDragging ? '0 0 10px 2px rgba(0, 0, 0, 0.2)' : 'none'
                }}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleIconDrop}
            >
                <Box component="label" sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {logoUrl ? (
                    <img src={logoUrl} alt="icon-preview" width="100" style={{ borderRadius: '10%' }} />
                ) : (
                    <IconPlaceholder/>
                )}
                <input type="file" hidden onChange={handleIconChange} />
                </Box>
            </Box>
        </Box>
        <Box sx={{ marginY: 2 }}>
            <Typography variant="h6">Screenshots</Typography>
            <Box
                sx={{
                    border: '2px dashed grey',
                    padding: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    minHeight: '150px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: isDragging ? '0 0 10px 2px rgba(0, 0, 0, 0.2)' : 'none'
                }}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleImagesDrop}
            >
                <Box component="label" sx={{ cursor: 'pointer', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {screenshots && screenshots.length > 0 ? (
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        {renderImagePreviews()}
                    </Grid>
                ) : (
                    <Grid container spacing={2} sx={{ marginTop: 2, alignItems: 'center', justifyContent: 'center' }}>
                        <Grid item>
                            <ScreenshotPlaceholder />
                        </Grid>
                        <Grid item>
                            <PhonePlaceholder />
                        </Grid>
                    </Grid>
                )}
                <input type="file" hidden multiple onChange={handleImagesChange} />
                </Box>
            </Box>
        </Box>
        <MDXEditor
            markdown={fileTypes.description!}
            onChange={handleDescriptionChange}
            plugins={[
                listsPlugin(),
                diffSourcePlugin(),
                headingsPlugin(),
                linkPlugin(),
                toolbarPlugin({
                    toolbarClassName: 'my-classname',
                    toolbarContents: () => (
                        <>
                            <DiffSourceToggleWrapper options={["rich-text", "source"]}>
                                {' '}
                                <UndoRedo />
                                <BlockTypeSelect/>
                                <BoldItalicUnderlineToggles />
                                <ListsToggle />
                            </DiffSourceToggleWrapper>
                        </>
                    )
                })
            ]}
        />
        <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            sx={{ marginTop: 2 }}
        >
            Save
        </Button>
        <Dialog
            open={isDialogOpen}
            onClose={handleCloseDialog}
        >
            <DialogTitle>Success</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    File type saved successfully!
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseDialog} color="primary">
                    OK
                </Button>
            </DialogActions>
        </Dialog>

        {/* License Options Overview Dialog */}
        <LicenseOptionsOverviewDialog 
            open={licenseOptionsOverviewOpen}
            onClose={() => {
                setLicenseOptionsOverviewOpen(false);
                // Reload license options after overview dialog closes to reflect any changes
                loadLicenseOptions();
            }}
            onDataChanged={() => {
                // Reload license options when data is changed in the overview dialog
                loadLicenseOptions();
            }}
        />
    </Box>
}

export default FileTypeEdit
